import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Save, Heart, Gift, HeartHandshake, Shield, Smartphone, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

const Step8SpecialWishes = ({ data, onNext, onPrevious, onSaveAndExit, saving, willId }) => {
  const [formData, setFormData] = useState({
    charitable_donation_enabled: data.charitable_donation_enabled || false,
    charitable_donation: data.charitable_donation || {
      organisation_name: '',
      contact_number: '',
      address: '',
      charity_amount: ''
    },
    organ_donation: data.organ_donation || false,
    organ_donation_details: data.organ_donation_details || {
      organisation_name: '',
      contact_number: '',
      address: ''
    },
    body_donation: data.body_donation || false,
    body_donation_details: data.body_donation_details || {
      organisation_name: '',
      contact_number: '',
      address: ''
    },
    personal_messages: data.personal_messages || '',
    include_survivorship_clause: data.include_survivorship_clause !== false, // Default to true
    include_simultaneous_death_clause: data.include_simultaneous_death_clause !== false, // Default to true
    include_digital_assets_clause: data.include_digital_assets_clause !== false // Default to true
  });

  const inputClass = "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 focus:ring-amber-500/20";
  const labelClass = "text-slate-300 font-medium text-sm";

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="step8-form">
      {/* Charitable Donations */}
      <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.charitable_donation_enabled}
            onChange={(e) => setFormData(prev => ({ ...prev, charitable_donation_enabled: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="charitable-donation-checkbox"
          />
          <div className="flex items-center gap-2">
            <Gift className="text-amber-400" size={20} />
            <span className="text-white font-medium text-lg">Charitable Donations</span>
          </div>
        </label>

        {formData.charitable_donation_enabled && (
          <div className="ml-8 mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-4">
            <div className="space-y-2">
              <Label className={labelClass}>Organisation Name</Label>
              <Input
                type="text"
                value={formData.charitable_donation.organisation_name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  charitable_donation: { ...prev.charitable_donation, organisation_name: e.target.value }
                }))}
                placeholder="Enter organisation name"
                className={inputClass}
                data-testid="charity-org-name"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Contact Number</Label>
              <Input
                type="tel"
                value={formData.charitable_donation.contact_number}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  charitable_donation: { ...prev.charitable_donation, contact_number: e.target.value }
                }))}
                placeholder="Enter contact number"
                className={inputClass}
                data-testid="charity-contact"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Address</Label>
              <textarea
                value={formData.charitable_donation.address}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  charitable_donation: { ...prev.charitable_donation, address: e.target.value }
                }))}
                placeholder="Enter organisation address"
                rows="2"
                className={`${inputClass} w-full rounded-md p-2`}
                data-testid="charity-address"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Charity Amount (₹)</Label>
              <Input
                type="number"
                value={formData.charitable_donation.charity_amount}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  charitable_donation: { ...prev.charitable_donation, charity_amount: e.target.value }
                }))}
                placeholder="Enter amount"
                className={inputClass}
                data-testid="charity-amount"
              />
            </div>
          </div>
        )}
      </div>

      {/* Organ Donation */}
      <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.organ_donation}
            onChange={(e) => setFormData(prev => ({ ...prev, organ_donation: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="organ-donation-checkbox"
          />
          <div>
            <div className="flex items-center gap-2">
              <Heart className="text-amber-400" size={20} />
              <span className="text-white font-medium text-lg">Organ Donation</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">I wish to donate my organs for medical purposes</p>
          </div>
        </label>

        {formData.organ_donation && (
          <div className="ml-8 mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-4">
            <div className="space-y-2">
              <Label className={labelClass}>Organisation Name</Label>
              <Input
                type="text"
                value={formData.organ_donation_details.organisation_name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  organ_donation_details: { ...prev.organ_donation_details, organisation_name: e.target.value }
                }))}
                placeholder="Enter organisation name"
                className={inputClass}
                data-testid="organ-org-name"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Contact Number</Label>
              <Input
                type="tel"
                value={formData.organ_donation_details.contact_number}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  organ_donation_details: { ...prev.organ_donation_details, contact_number: e.target.value }
                }))}
                placeholder="Enter contact number"
                className={inputClass}
                data-testid="organ-contact"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Address</Label>
              <textarea
                value={formData.organ_donation_details.address}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  organ_donation_details: { ...prev.organ_donation_details, address: e.target.value }
                }))}
                placeholder="Enter organisation address"
                rows="2"
                className={`${inputClass} w-full rounded-md p-2`}
                data-testid="organ-address"
              />
            </div>
          </div>
        )}
      </div>

      {/* Body Donation */}
      <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.body_donation}
            onChange={(e) => setFormData(prev => ({ ...prev, body_donation: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="body-donation-checkbox"
          />
          <div>
            <div className="flex items-center gap-2">
              <HeartHandshake className="text-amber-400" size={20} />
              <span className="text-white font-medium text-lg">Body Donation</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">I wish to donate my body for medical research</p>
          </div>
        </label>

        {formData.body_donation && (
          <div className="ml-8 mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700 space-y-4">
            <div className="space-y-2">
              <Label className={labelClass}>Organisation Name</Label>
              <Input
                type="text"
                value={formData.body_donation_details.organisation_name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  body_donation_details: { ...prev.body_donation_details, organisation_name: e.target.value }
                }))}
                placeholder="Enter organisation name"
                className={inputClass}
                data-testid="body-org-name"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Contact Number</Label>
              <Input
                type="tel"
                value={formData.body_donation_details.contact_number}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  body_donation_details: { ...prev.body_donation_details, contact_number: e.target.value }
                }))}
                placeholder="Enter contact number"
                className={inputClass}
                data-testid="body-contact"
              />
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Address</Label>
              <textarea
                value={formData.body_donation_details.address}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  body_donation_details: { ...prev.body_donation_details, address: e.target.value }
                }))}
                placeholder="Enter organisation address"
                rows="2"
                className={`${inputClass} w-full rounded-md p-2`}
                data-testid="body-address"
              />
            </div>
          </div>
        )}
      </div>

      {/* Legal Clauses Section */}
      <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-4">
        <h3 className="text-white font-medium text-lg mb-2">Legal Clauses</h3>
        <p className="text-sm text-slate-400 mb-4">Include standard legal clauses in your will for added protection</p>
        
        {/* Survivorship Clause Toggle */}
        <label className="flex items-start gap-3 cursor-pointer p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
          <input
            type="checkbox"
            checked={formData.include_survivorship_clause}
            onChange={(e) => setFormData(prev => ({ ...prev, include_survivorship_clause: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="survivorship-clause-checkbox"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Shield className="text-amber-400" size={20} />
              <span className="text-white font-medium">Survivorship Clause</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              If any beneficiary does not survive you by 30 days, their share will become part of your residuary estate 
              (unless a contingent beneficiary is named). This prevents assets from being distributed to the estate of a 
              beneficiary who passes away shortly after you.
            </p>
          </div>
        </label>

        {/* Simultaneous Death Clause Toggle */}
        <label className="flex items-start gap-3 cursor-pointer p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
          <input
            type="checkbox"
            checked={formData.include_simultaneous_death_clause}
            onChange={(e) => setFormData(prev => ({ ...prev, include_simultaneous_death_clause: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="simultaneous-death-clause-checkbox"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={20} />
              <span className="text-white font-medium">Simultaneous Death Clause</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              If you and a beneficiary die at the same time (e.g., in a common accident) or the order of death cannot be 
              determined, the beneficiary will be deemed to have predeceased you. Their share will go to the residuary 
              estate or any named substitute beneficiary.
            </p>
          </div>
        </label>

        {/* Digital Assets Clause Toggle */}
        <label className="flex items-start gap-3 cursor-pointer p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
          <input
            type="checkbox"
            checked={formData.include_digital_assets_clause}
            onChange={(e) => setFormData(prev => ({ ...prev, include_digital_assets_clause: e.target.checked }))}
            className="w-5 h-5 mt-0.5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
            data-testid="digital-assets-clause-checkbox"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Smartphone className="text-amber-400" size={20} />
              <span className="text-white font-medium">Digital Assets Clause</span>
            </div>
            <p className="text-slate-400 text-sm mt-1">
              Authorizes your Executor to manage, distribute, or delete your digital assets including email accounts, 
              social media profiles, cloud storage, and cryptocurrency wallets that are not specifically mentioned in 
              other sections of your will.
            </p>
          </div>
        </label>
      </div>

      {/* Personal Message / Final Wishes */}
      <div className="bg-slate-800/50 p-5 rounded-lg border border-slate-700 space-y-4">
        <Label className="text-white font-medium text-lg">Personal Message / Final Wishes</Label>
        <p className="text-sm text-slate-400">Leave any personal messages or final wishes for your loved ones</p>
        <textarea
          value={formData.personal_messages}
          onChange={(e) => setFormData(prev => ({ ...prev, personal_messages: e.target.value }))}
          rows="5"
          placeholder="Share your thoughts, wishes, or any final messages for your family and loved ones..."
          className={`${inputClass} w-full rounded-md p-3`}
          data-testid="personal-messages"
        />
      </div>

      {/* Navigation */}
      <div className="flex flex-wrap justify-between gap-4 pt-6 border-t border-slate-700">
        <div className="flex gap-2">
          <Button type="button" onClick={onPrevious} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
            <ChevronLeft size={16} className="mr-2" /> Previous
          </Button>
          <Button type="button" onClick={() => onSaveAndExit(formData)} disabled={saving} variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-500/10">
            <Save size={16} className="mr-2" /> Save & Exit
          </Button>
        </div>
        <Button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
          Next: Declaration & Storage <ChevronRight size={16} className="ml-2" />
        </Button>
      </div>
    </form>
  );
};

export default Step8SpecialWishes;
